import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { withTrackProps } from '@dpdgroupuk/react-event-tracker';

import SimpleModal from '../../SimpleModal';
import Button from '../../Button';
import {
  IN_BUILD_CONTINUE_WARNING_$,
  IN_BUILD_CONTINUE_WARNING,
  WARNING,
  CONTINUE,
  CANCEL,
  LOGGED_TO_REVIEW,
  DO_YOU_WANT_TO_CONTINUE,
  WILL_INCREASE_COSTS,
} from '../../../constants/strings';
import { Text, View } from '../../Base';
import { IN_BUILD_WARNING } from '../../../constants/analytics';
import warningTextModal from '../../styles/warningTextModal';
import { formatMessage } from '../../../utils/string';
import { partiallyCaddedStops } from '../../../store/reducers/route';

const useStyles = makeStyles(() => ({
  ...warningTextModal,
  stopListContainer: {
    maxHeight: 'calc(100% - 283px)',
    minHeight: '21px',
    overflow: 'auto',
    marginBottom: '28px',
  },
  root: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: '600px !important',
    },
  },
  truncateText: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fullWidth: {
    maxWidth: '100%',
  },
  stopMessage: {
    display: 'flex',
  },
}));

const InBuildWarningModal = ({ open, onClose, onConfirm, routeId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [stops, setAffectedStops] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    const fetchPartiallyCaddedStops = async () => {
      const stops = await dispatch(partiallyCaddedStops(routeId));
      stops && setAffectedStops(stops);
      setIsDataReady(true);
    };

    fetchPartiallyCaddedStops();
  }, [routeId, dispatch]);

  const ModalContent = useMemo(() => {
    if (isDataReady) {
      if (!stops.length) {
        return (
          <Text className={classes.text}>{IN_BUILD_CONTINUE_WARNING}</Text>
        );
      }
      return (
        <>
          <Text className={classes.text}>
            {formatMessage(IN_BUILD_CONTINUE_WARNING_$, stops.length)}
          </Text>
          <View className={classes.stopListContainer} data-testid="stop-list">
            {stops.map(({ addressLine, parcelCount, postCode }, index) => (
              <View key={index} className={classes.stopMessage}>
                <Text
                  className={clsx(
                    classes.text,
                    classes.bold,
                    classes.noPadding
                  )}
                >
                  {`Stop - `}
                </Text>
                <Text
                  className={clsx(
                    classes.text,
                    classes.bold,
                    classes.noPadding,
                    classes.truncateText
                  )}
                >{`${addressLine || ''}`}</Text>
                <Text
                  className={clsx(
                    classes.text,
                    classes.bold,
                    classes.noPadding
                  )}
                >
                  {` ${postCode || ''} - ${parcelCount} Parcel(s)`}
                </Text>
              </View>
            ))}
          </View>
          <Text className={classes.text}>{WILL_INCREASE_COSTS}</Text>
          <Text className={classes.text}>{LOGGED_TO_REVIEW}</Text>
          <Text className={classes.text}>{DO_YOU_WANT_TO_CONTINUE}</Text>
        </>
      );
    }
    return null;
  }, [isDataReady, stops]);

  return (
    <SimpleModal
      onClose={onClose}
      open={open}
      title={WARNING.toUpperCase()}
      loadId={IN_BUILD_WARNING.LOAD}
      rootClassName={classes.root}
      actions={
        <>
          <Button
            minWidthZero
            onClick={onClose}
            className={classes.yellowButton}
          >
            {CANCEL}
          </Button>
          <Button
            minWidthZero
            onClick={onConfirm}
            className={classes.blueButton}
          >
            {CONTINUE}
          </Button>
        </>
      }
    >
      {!isDataReady && (
        <View className={classes.loadingContainer}>
          <CircularProgress />
        </View>
      )}
      <View className={classes.fullWidth}>{ModalContent}</View>
    </SimpleModal>
  );
};

export default withTrackProps({
  onClose: IN_BUILD_WARNING.CLICK_CANCEL,
  onConfirm: IN_BUILD_WARNING.CLICK_CONTINUE,
})(InBuildWarningModal);
